import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MapPin, Star } from "lucide-react";
import { WhatsAppButton, PreventivoButton, EmailButton } from "./CTAs";
import { WHATSAPP_ENABLED } from "../../config";

const titleLines = [
  "Piccoli lavori edili",
  "e ristrutturazioni",
  "rapide a Roma",
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden bg-white pt-24 sm:pt-28 lg:pt-32 pb-20 sm:pb-24 lg:pb-28">
      {/* soft spotlight */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute top-20 right-0 h-[420px] w-[420px] rounded-full bg-navy/5 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy/70"
          >
            <MapPin size={14} className="text-brand-orange" /> Interventi in tutta Roma
          </motion.div>

          <h1 className="mt-6 font-display font-extrabold tracking-tighter text-navy text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-[4.4rem]">
            {titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed"
          >
            Tinteggiature, rifacimento bagni, piccoli lavori di muratura e manutenzioni
            eseguiti con professionalità, rapidità e attenzione ai dettagli.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3"
          >
            <WhatsAppButton size="lg" testid="hero-whatsapp" label="Preventivo su WhatsApp" location="hero" />
            <PreventivoButton size="lg" variant="outlineDark" testid="hero-preventivo" label="Preventivo Online" location="hero" />
            <EmailButton size="lg" variant="outlineDark" testid="hero-email" label="Richiesta via Email" location="hero" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex items-center gap-4 text-sm text-ink/60"
          >
            <div className="flex items-center gap-1 text-brand-orange">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <span>Clienti soddisfatti a Roma · Preventivo gratuito</span>
          </motion.div>
        </div>

        {/* Hero image, clipped frame + parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-2xl shadow-2xl aspect-[4/5] sm:aspect-[4/5]">
            <motion.img
              src="https://images.pexels.com/photos/6764270/pexels-photo-6764270.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=940"
              alt="Imbianchino al lavoro su una parete a Roma"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
          </div>

          {/* floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="absolute bottom-4 -left-3 sm:-left-6 bg-white rounded-xl shadow-xl border border-gray-100 px-5 py-4"
          >
            <p className="text-3xl font-display font-extrabold text-navy leading-none">24/7</p>
            <p className="text-xs text-ink/60 mt-1">{WHATSAPP_ENABLED ? <>Richieste WhatsApp<br />sempre attive</> : <>Richieste online<br />sempre aperte</>}</p>
          </motion.div>
          <div className="absolute -top-4 -right-3 sm:-right-5 bg-brand-orange text-white rounded-xl shadow-xl px-4 py-3">
            <p className="text-sm font-semibold leading-tight">Risposta<br />rapida</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
